/* eslint-disable */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ReactGA from 'react-ga';
import Modal from '../../components/modal/Modal';
import { shareViaApps } from '../../utils/shareViaApps';
import {
  createPortStickerPreviewDataUrl,
  START_PAGE_STICKER_PRINT_ORDER_URL,
} from '../startPage/startPagePortStickerPng';
import { buildStartPageShareUrl } from '../startPage/StartPageShareButton';
import '../startPage/startPage.css';

export function parseStartManyPortsParam(raw) {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
}

export function buildStartManyShareUrl(ports) {
  const list = Array.isArray(ports) ? ports.map(String) : parseStartManyPortsParam(ports);
  const query = list.length ? `?ports=${list.join(',')}` : '';
  if (typeof window === 'undefined') {
    return `/startMany${query}`;
  }
  return `${window.location.origin}/startMany${query}`;
}

export default function StartManyShareButton({
  ports,
  className = 'layout-page-chrome-btn',
  showPrintOrder = false,
  printStationNumber,
}) {
  const { t } = useTranslation();
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const portList = useMemo(() => {
    if (Array.isArray(ports)) {
      return ports.map(p => String(p).trim()).filter(Boolean);
    }
    return parseStartManyPortsParam(ports);
  }, [ports]);

  const shareUrl = useMemo(() => buildStartManyShareUrl(portList), [portList]);
  const portsLabel = useMemo(() => portList.join(', '), [portList]);
  const stickerStationNumber = String(printStationNumber ?? portList[0] ?? '').trim();
  const portRange = useMemo(() => {
    const sorted = [...portList].sort((a, b) => Number(a) - Number(b));
    if (sorted.length < 2) return sorted[0] || '';
    return `${sorted[0]}-${sorted[sorted.length - 1]}`;
  }, [portList]);
  const portTitle =
    !showPrintOrder && portList.length > 1
      ? `220-km.com\n${t('startPageShareStickerPortLabel').trim()}\n${portRange}`
      : `220-km.com\n${t('startPageShareStickerPortLabel')}${stickerStationNumber}`;
  const qrTargetUrl = useMemo(
    () => (showPrintOrder && stickerStationNumber ? buildStartPageShareUrl(stickerStationNumber) : shareUrl),
    [showPrintOrder, stickerStationNumber, shareUrl]
  );

  useEffect(() => {
    if (!qrOpen || !qrTargetUrl) {
      setQrDataUrl('');
      return undefined;
    }

    let active = true;
    const preview = createPortStickerPreviewDataUrl({
      stationNumber: stickerStationNumber || portList[0],
      shareUrl: qrTargetUrl,
      portTitle,
    });

    preview
      .then(url => {
        if (active) setQrDataUrl(url);
      })
      .catch(() => {
        if (active) setQrDataUrl('');
      });

    return () => {
      active = false;
    };
  }, [qrOpen, qrTargetUrl, showPrintOrder, stickerStationNumber, portTitle]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrTargetUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleShareClick = () => {
    ReactGA.event({
      category: 'share',
      action: 'start many share modal',
      label: portsLabel,
    });
    void copyLink();
    setQrOpen(true);
  };

  const handleShareViaApps = () => {
    ReactGA.event({
      category: 'share',
      action: 'start many share via apps',
      label: portsLabel,
    });
    void shareViaApps({
      title: t('startManyShareTitle'),
      text: t('startManyShareText', { ports: portsLabel }),
      url: qrTargetUrl,
    });
  };

  if (!portList.length) {
    return null;
  }

  return (
    <>
      <button type="button" className={className} aria-label={t('startManyShareBtn')} onClick={handleShareClick}>
        <ShareOutlinedIcon className="layout-page-chrome-btn__icon" aria-hidden />
      </button>

      <Modal modalOpen={qrOpen} setModalOpen={setQrOpen} scrollable>
        <div
          className="start-page-share-qr-modal"
          role="dialog"
          aria-modal="true"
          aria-label={t('startManyShareTitle')}
        >
          {qrDataUrl ? (
            <button
              type="button"
              className="start-page-share-qr-copy-hit"
              onClick={copyLink}
              aria-label={copied ? t('b2bMarketplaceShareCopied') : t('b2bSectionShareCopyLink')}
            >
              <img
                src={qrDataUrl}
                alt={t('startPageShareStickerPreviewAlt', { port: portsLabel || stickerStationNumber })}
                className="start-page-share-sticker-preview"
              />
            </button>
          ) : (
            <div className="start-page-share-sticker-preview start-page-share-sticker-preview--loading" aria-hidden />
          )}
          <button type="button" className="start-page-share-qr-url" onClick={copyLink}>
            {copied ? t('b2bMarketplaceShareCopied') : qrTargetUrl}
          </button>
          <div className="start-page-share-qr-actions">
            <button
              type="button"
              className="b2b-btn b2b-btn--outline start-page-share-qr-download-btn"
              onClick={handleShareViaApps}
            >
              {t('b2bMarketplaceShareBtn')}
            </button>
          </div>
          {showPrintOrder ? (
            <p className="start-page-share-qr-print-order">
              <a href={START_PAGE_STICKER_PRINT_ORDER_URL} target="_blank" rel="noopener noreferrer">
                {t('startPageSharePrintOrder')}
              </a>
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
